/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import { Currency } from '@configs/language';
import {
  Countries,
  Currencies,
  ExtendedCurrencies,
  NON_SWIFT_COUNTRIES_ISO_CODES,
} from '@constants/enums';
import {
  onlyNumbersAndLettersRegex,
  onlyNumbersRegex,
  taxIdRegex,
  textRegex,
} from '@constants/general';
import clientSvc from '@efex-services/client/clientSvc';
import { Button } from '@efexteam/components';
import {
  Alert,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  selectBusiness,
  selectProfileData,
} from '@pages/profile/store/selectors';
import {
  validateIban,
  validateSpaces,
  validatePoBox,
} from '@utils/validations';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import DataLoadingWrapper from '../../../../components/common/DataLoadingWrapper';
import {
  CHINA_SWIFT_CURRENCIES,
  CountryBankAccount,
  JAPAN_SWIFT_CURRENCIES,
  TRANSFER_METHOD_OPTIONS,
  dropdownOptionsCurrency,
  dropdownOptionsSWIFTCurrency,
} from '../../../../components/types';
import authSvc from '../../../../services/auth/authSvc';
import {
  BANK_NAME_MAX_LENGTH,
  COUNTRIES_IN_EUROPE,
  CountriesToShowDefaultCurrency,
  MX_DIGITS_CLABE,
  USA_DIGITS_ACCOUNT_MAX,
  USA_DIGITS_ACCOUNT_MIN,
  USA_DIGITS_ROUTING,
  providerRelation,
} from '../constants';
import { useFormData } from '../context';
import {
  isUnsupportedClabe,
  getUnsupportedBankInfo,
} from '@/components/payables/PaymentModal/utils';

const ALLOWED_ROUTING_NUMBERS = ['122100024', '021000021'];

interface IBankData {
  formStep: number;
  loading: boolean;
  error: Error | undefined;
  nextFormStep: () => void;
  prevStep: () => void;
  retry: () => void;
  close: () => void;
}

type FormData = {
  bankName: string;
  bankCountryCode: string;
  accountNumber: string;
  routingNumber: string;
  clabe: string;
  rfc: string;
  swiftCode: string;
  accountType: string;
  bankCode: string;
  documentType: string;
  identificationNumber: string;
  taxId: string;
  accountCurrency: Currency;
  transferMethod: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  postCode?: string;
  providerRelation?: string;
};

const BankData = ({
  formStep,
  loading,
  error,
  nextFormStep,
  prevStep,
  close,
}: IBankData) => {
  const company = useSelector(selectBusiness);
  const { isAvailableChinaPayment } = useSelector(selectProfileData) || {};
  const [isCountryInEurope, setIsCountryInEurope] = useState<boolean>(false);
  const [bankCountryCodeValue, setBankCountryCodeValue] = useState<
    number | null
  >(null);
  const [bankIsoCode, setBankIsoCode] = useState<string | null>(null);
  const [isValidIban, setIsValidIban] = useState<boolean>(false);

  const { t } = useTranslation();
  const { setFormValues, data } = useFormData();
  const { country, entity, countryId } = data;
  const typeAccount:
    | CountryBankAccount.MX
    | CountryBankAccount.USA
    | CountryBankAccount.COL =
    CountryBankAccount[(country as keyof typeof CountryBankAccount) || 'USA'];
  const [colombianBanks, setColombianBanks] = useState<null | any[]>(null);
  const [accountCurrecySelected, setAccountCurrencySelected] = useState<
    Currency | undefined
  >(undefined);
  const [routingValidation, setRoutingValidation] = useState({
    routingNumber: '',
    transferMethod: '',
  });

  const dropdownOptions: { key: string; value: string }[] = [
    { value: '1', key: t('checkingsAccount') },
    { value: '2', key: t('savingsAccount') },
  ];

  const documentTypeOptions: { key: string; value: string }[] = [
    { value: 'CC', key: t('CCType') },
    { value: 'CE', key: t('CEType') },
    { value: 'NIT', key: t('NITType') },
    { value: 'PASS', key: t('PASSType') },
    { value: 'PEP', key: t('PEPType') },
  ];

  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    control,
  } = useForm<FormData>({ mode: 'all' });

  const { data: swiftDropdownOptions, invoke: getSwiftCountries } = useApiCall(
    () => clientSvc.getSwiftCountries('all')
  );

  const {
    invoke: validateRoutingNumber,
    error: errorRoutingValidation,
    loading: loadingRoutingValidation,
  } = useApiCall(
    ({
      routingNumber,
      transferMethod,
    }: {
      routingNumber: string;
      transferMethod: string;
    }) =>
      clientSvc.validateRoutingNumber({
        routing_number: routingNumber,
        payment_type: transferMethod,
      })
  );

  const onSubmit = handleSubmit((values) => {
    setFormValues(values);
  });

  const handleBack = () => {
    prevStep();
  };

  const validateValueFromKeyboard = (e: any) => {
    const problematicKeys = ['e', 'E', '-', '+', 'ArrowUp', 'ArrowDown'];
    if (problematicKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const getBanks = async () => {
    try {
      const result = await authSvc.getBanks();
      setColombianBanks(result);
      setValue('bankName', result[0].name);
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (event: {
    target: {
      name: string;
      value: string;
    };
  }) => {
    if (event.target.name === 'bankCode') {
      const bankName =
        (colombianBanks?.find((item) => item.code === event.target.value)
          .name as string) || '';
      setValue('bankName', bankName);
    }
  };

  const updateRoutingValues = (value: string, type: string) => {
    setRoutingValidation((prevState) => ({
      ...prevState,
      [type]: value,
    }));
  };

  const defaultCurrency = (selectedCountry: string): Currency => {
    if (COUNTRIES_IN_EUROPE.includes(selectedCountry)) {
      return ExtendedCurrencies.EUR;
    }

    const countryCurrencyMap: Record<string, Currency> = {
      [CountriesToShowDefaultCurrency.GB]: ExtendedCurrencies.GBP,
      [CountriesToShowDefaultCurrency.CN]: ExtendedCurrencies.CNY,
      [CountriesToShowDefaultCurrency.AU]: ExtendedCurrencies.AUD,
      [CountriesToShowDefaultCurrency.PL]: ExtendedCurrencies.PLN,
    };

    return countryCurrencyMap[selectedCountry] || Currencies.USD;
  };

  const COUNTRIES_SWIFT_DROPDOWN_OPTIONS = useMemo(() => {
    if (isAvailableChinaPayment) {
      return swiftDropdownOptions;
    }
    return swiftDropdownOptions?.filter(
      (option: any) => option.isoCode !== CountriesToShowDefaultCurrency.CN
    );
  }, [isAvailableChinaPayment, swiftDropdownOptions]);

  const COUNTRIES_CURRENCIES_OPTIONS = useMemo(() => {
    if (bankIsoCode === CountriesToShowDefaultCurrency.CN) {
      return CHINA_SWIFT_CURRENCIES;
    }
    if (bankIsoCode === CountriesToShowDefaultCurrency.JP) {
      return [...JAPAN_SWIFT_CURRENCIES, ...dropdownOptionsSWIFTCurrency];
    }
    return dropdownOptionsSWIFTCurrency;
  }, [bankIsoCode]);

  useEffect(() => {
    if (
      (data.bankName || data.bankCode || data.rfc) &&
      (data.accountNumber ||
        data.clabe ||
        data.routingNumber ||
        data.taxId ||
        data.swiftCode)
    ) {
      nextFormStep();
    }
    if (data.country === 'COL') {
      getBanks();
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      setValue('address', data.address || undefined);
      setValue('address2', data.address2 || undefined);
      setValue('city', data.city || undefined);
      setValue('state', data.state || undefined);
      setValue('postCode', data.postCode || undefined);
    }
  }, [data]);

  useEffect(() => {
    if (countryId) {
      getSwiftCountries();
    }
  }, [countryId]);

  useEffect(() => {
    if (COUNTRIES_SWIFT_DROPDOWN_OPTIONS?.length) {
      const countrySelected = COUNTRIES_SWIFT_DROPDOWN_OPTIONS.find(
        (item: any) => item.id === countryId
      );

      setValue('accountCurrency', defaultCurrency(countrySelected?.isoCode));
      setIsCountryInEurope(true);
      setBankCountryCodeValue(countrySelected?.id);
      setBankIsoCode(countrySelected?.isoCode);
    }
  }, [COUNTRIES_SWIFT_DROPDOWN_OPTIONS]);

  useEffect(() => {
    if (routingValidation.routingNumber && routingValidation.transferMethod) {
      if (ALLOWED_ROUTING_NUMBERS.includes(routingValidation.routingNumber)) {
        return;
      }
      validateRoutingNumber({
        routingNumber: routingValidation.routingNumber,
        transferMethod:
          routingValidation.transferMethod === '2' ? 'ACH' : 'WIRE',
      });
    }
  }, [routingValidation]);

  const handleChangeAccountNumber = (accountNumber: string | undefined) => {
    if (isCountryInEurope && accountNumber) {
      const isValid = validateIban(accountNumber);
      if (isValid) {
        const countryCode = accountNumber.substring(0, 2);
        const countryOption = COUNTRIES_SWIFT_DROPDOWN_OPTIONS.find(
          (item: any) => item.isoCode === countryCode
        );
        if (countryOption) {
          setBankCountryCodeValue(countryOption.id);
          setBankIsoCode(countryOption.isoCode);
          setIsValidIban(true);
        }
      }
      setIsValidIban(isValid);
    }
  };

  useEffect(() => {
    if (bankCountryCodeValue) {
      setValue('bankCountryCode', bankCountryCodeValue.toString());
    }
  }, [bankCountryCodeValue]);

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t('ccProviderTitle')}
      </Typography>

      <form onSubmit={onSubmit} autoComplete="off">
        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          {!countryId ? (
            <>
              {data.country !== 'COL' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      id="bankName"
                      type="bankName"
                      label={t('bankName')}
                      error={!!errors.bankName}
                      {...register('bankName', {
                        required: true,
                        maxLength: BANK_NAME_MAX_LENGTH,
                        pattern: {
                          value: textRegex,
                          message: '',
                        },
                        validate: {
                          spaces: (value) => validateSpaces(value, t),
                        },
                      })}
                    />
                    {errors.bankName && errors.bankName.type === 'required' && (
                      <p className="text-orange-600 text-xs">
                        {t('defaultError')}
                      </p>
                    )}
                    {errors.bankName &&
                      errors.bankName.type === 'maxLength' && (
                        <p className="text-orange-600 text-xs">
                          {t('maxLength150Characters')}
                        </p>
                      )}
                    {errors.bankName && errors.bankName.type === 'pattern' && (
                      <p className="text-orange-600 text-xs">
                        {t('ccWrongBankName')}
                      </p>
                    )}
                    {errors.bankName && errors.bankName.type === 'spaces' && (
                      <p className="text-orange-600 text-xs">
                        {errors.bankName.message as string}
                      </p>
                    )}
                  </FormControl>
                </Grid>
              )}
              {data.country === 'COL' && (
                <>
                  {colombianBanks && (
                    <Grid item xs={12}>
                      <FormControl fullWidth className="!mb-3">
                        <InputLabel id="bankCode">{t('bank')}</InputLabel>
                        <Select
                          labelId="bankCode"
                          id="bankCode"
                          error={!!errors.bankCode}
                          defaultValue={colombianBanks[0].code}
                          label={t('bank')}
                          {...register('bankCode', { required: true })}
                          onChange={handleChange}
                        >
                          {colombianBanks?.map((bankCode) => (
                            <MenuItem key={bankCode.code} value={bankCode.code}>
                              {bankCode.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.bankCode && (
                          <p className="text-orange-600 text-xs">
                            {t('defaultError')}
                          </p>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControl fullWidth className="!mb-3">
                      <InputLabel id="accountType">
                        {t('accountType')}
                      </InputLabel>
                      <Select
                        labelId="accountType"
                        id="accountType"
                        error={!!errors.accountType}
                        defaultValue={
                          dropdownOptions[0].value ?? dropdownOptions[0].value
                        }
                        label={t('accountType')}
                        {...register('accountType', { required: true })}
                      >
                        {dropdownOptions.map((accountType) => (
                          <MenuItem
                            key={accountType.key}
                            value={accountType.value}
                          >
                            {accountType.key}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.accountType && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth className="!mb-3">
                      <TextField
                        id="accountNumber"
                        type="number"
                        onKeyDown={(e) => validateValueFromKeyboard(e)}
                        label={t('account_number')}
                        error={!!errors.accountNumber}
                        {...register('accountNumber', {
                          required: true,
                        })}
                      />
                      {errors.accountNumber && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth className="!mb-3">
                      <InputLabel id="bankCode">{t('documentType')}</InputLabel>
                      <Select
                        labelId="documentType"
                        id="documentType"
                        error={!!errors.documentType}
                        defaultValue={documentTypeOptions[0].value}
                        label={t('documentType')}
                        {...register('documentType', { required: true })}
                        onChange={handleChange}
                      >
                        {documentTypeOptions?.map((document) => (
                          <MenuItem key={document.key} value={document.value}>
                            {document.key}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.bankCode && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth className="!mb-3">
                      <TextField
                        id="identificationNumber"
                        type="number"
                        onKeyDown={(e) => validateValueFromKeyboard(e)}
                        label={t('identificationNumber')}
                        error={!!errors.identificationNumber}
                        {...register('identificationNumber', {
                          required: true,
                        })}
                      />
                      {errors.identificationNumber && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                </>
              )}
              {data.country !== 'COL' && (
                <Grid item xs={12} className={country !== 'MX' ? '' : 'hidden'}>
                  <FormControl fullWidth>
                    <TextField
                      id="accountNumber"
                      type="number"
                      label={t('ccAccountNumber')}
                      error={!!errors.accountNumber}
                      onKeyDown={(e) => validateValueFromKeyboard(e)}
                      onFocus={(e) => {
                        const handleWheel = (wheelEvent: Event) => {
                          wheelEvent.preventDefault();
                        };
                        e.target.addEventListener('wheel', handleWheel, {
                          passive: false,
                        });

                        const handleBlur = () => {
                          e.target.removeEventListener('wheel', handleWheel);
                          e.target.removeEventListener('blur', handleBlur);
                        };
                        e.target.addEventListener('blur', handleBlur);
                      }}
                      {...register('accountNumber', {
                        required: country !== 'MX',
                        minLength: USA_DIGITS_ACCOUNT_MIN,
                        maxLength: USA_DIGITS_ACCOUNT_MAX,
                      })}
                    />
                    {errors.accountNumber &&
                      errors.accountNumber.type === 'required' && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    {errors.accountNumber &&
                      errors.accountNumber.type === 'minLength' && (
                        <p className="text-orange-600 text-xs">
                          {t('accountNumberValidationUS')}
                        </p>
                      )}
                    {errors.accountNumber &&
                      errors.accountNumber.type === 'maxLength' && (
                        <p className="text-orange-600 text-xs">
                          {t('accountNumberValidationUS')}
                        </p>
                      )}
                  </FormControl>
                </Grid>
              )}
              {typeAccount === 'clabe' && (
                <Grid item xs={12} md={entity !== 'Person' ? 6 : 12}>
                  <FormControl fullWidth>
                    <TextField
                      id="clabe"
                      type="number"
                      label={t('accountClabe') as string}
                      error={!!errors.clabe}
                      {...register('clabe', {
                        required: true,
                        minLength: MX_DIGITS_CLABE,
                        maxLength: MX_DIGITS_CLABE,
                        validate: {
                          notUnsupportedBank: (value) => {
                            if (value && isUnsupportedClabe(value)) {
                              const bankInfo = getUnsupportedBankInfo(value);
                              return bankInfo
                                ? (t('clabeUnsupportedBankSpecific', {
                                    bankName: bankInfo.name,
                                  }) as string)
                                : (t('clabeUnsupportedBank') as string);
                            }
                            return true;
                          },
                        },
                      })}
                    />
                    {errors.clabe && errors.clabe.type === 'required' && (
                      <p className="text-orange-600 text-xs">
                        {t('defaultError')}
                      </p>
                    )}
                    {errors.clabe && errors.clabe.type === 'minLength' && (
                      <p className="text-orange-600 text-xs">
                        {t('clabeValidation')} ({MX_DIGITS_CLABE})
                      </p>
                    )}
                    {errors.clabe && errors.clabe.type === 'maxLength' && (
                      <p className="text-orange-600 text-xs">
                        {t('clabeValidation')} ({MX_DIGITS_CLABE})
                      </p>
                    )}
                    {errors.clabe &&
                      errors.clabe.type === 'notUnsupportedBank' && (
                        <p className="text-orange-600 text-xs">
                          {errors.clabe.message as string}
                        </p>
                      )}
                  </FormControl>
                </Grid>
              )}

              {typeAccount === 'clabe' && entity !== 'Person' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="currency">
                      {t('ccAccountCurrencyType')}
                    </InputLabel>
                    <Controller
                      name="accountCurrency"
                      control={control}
                      defaultValue={dropdownOptionsCurrency[0].value}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          labelId="accountCurrency"
                          id="accountCurrency"
                          defaultValue={dropdownOptionsCurrency[0].value}
                          label={t('ccAccountCurrencyType')}
                          value={value}
                          onChange={(val) => {
                            onChange(val);
                            setAccountCurrencySelected(
                              val.target.value as Currency
                            );
                          }}
                        >
                          {dropdownOptionsCurrency.map((currency) => (
                            <MenuItem key={currency.key} value={currency.value}>
                              {currency.key}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.accountCurrency && (
                      <p className="text-orange-600 text-xs">
                        {t('defaultError')}
                      </p>
                    )}
                  </FormControl>
                </Grid>
              )}
              {typeAccount === 'routingNumber' && (
                <Grid item xs={12}>
                  <Alert severity="warning" className="w-full !mb-5 !mt-1">
                    {t('ccRegisterUserUsaRoutingWarning')}
                  </Alert>
                  <FormControl fullWidth>
                    <Controller
                      name="routingNumber"
                      control={control}
                      defaultValue={dropdownOptionsCurrency[0].value}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          id="routingNumber"
                          type="number"
                          label={
                            t(`contactPlaceholderAccount${country}`) as string
                          }
                          onKeyDown={(e) => validateValueFromKeyboard(e)}
                          onFocus={(e) => {
                            const handleWheel = (wheelEvent: Event) => {
                              wheelEvent.preventDefault();
                            };
                            e.target.addEventListener('wheel', handleWheel, {
                              passive: false,
                            });

                            const handleBlur = () => {
                              e.target.removeEventListener(
                                'wheel',
                                handleWheel
                              );
                              e.target.removeEventListener('blur', handleBlur);
                            };
                            e.target.addEventListener('blur', handleBlur);
                          }}
                          value={value}
                          error={
                            !!errors.routingNumber || !!errorRoutingValidation
                          }
                          {...register('routingNumber', {
                            required: true,
                            minLength: USA_DIGITS_ROUTING,
                            maxLength: USA_DIGITS_ROUTING,
                          })}
                          onChange={(e) => {
                            onChange(e);
                            updateRoutingValues(
                              e.target.value,
                              'routingNumber'
                            );
                          }}
                        />
                      )}
                    />
                    {errors.routingNumber &&
                      errors.routingNumber.type === 'required' && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    {errors.routingNumber &&
                      errors.routingNumber.type === 'minLength' && (
                        <p className="text-orange-600 text-xs">
                          {t('clabeValidation')}
                        </p>
                      )}
                    {errors.routingNumber &&
                      errors.routingNumber.type === 'maxLength' && (
                        <p className="text-orange-600 text-xs">
                          {t('clabeValidation')}
                        </p>
                      )}
                    {!errors.routingNumber &&
                      errorRoutingValidation &&
                      (errorRoutingValidation as any).response.data.message && (
                        <p className="text-orange-600 text-xs">
                          {
                            (errorRoutingValidation as any).response.data
                              .message
                          }
                        </p>
                      )}
                  </FormControl>
                </Grid>
              )}
              {data.country === Countries.USA && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth className="!mb-3">
                      <InputLabel id="accountType">
                        {t('accountType')}
                      </InputLabel>
                      <Select
                        labelId="accountType"
                        id="accountType"
                        error={!!errors.accountType}
                        defaultValue={
                          dropdownOptions[0].value ?? dropdownOptions[0].value
                        }
                        label={t('accountType')}
                        {...register('accountType', { required: true })}
                      >
                        {dropdownOptions.map((accountType) => (
                          <MenuItem
                            key={accountType.key}
                            value={accountType.value}
                          >
                            {accountType.key}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.accountType && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth className="!mb-3">
                      <InputLabel id="transferMethod">
                        {t('transferMethod')}
                      </InputLabel>
                      <Controller
                        name="transferMethod"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            labelId="transferMethod"
                            id="transferMethod"
                            error={!!errors.transferMethod}
                            label={t('transferMethod')}
                            value={value}
                            {...register('transferMethod', { required: true })}
                            onChange={(e) => {
                              onChange(e);
                              updateRoutingValues(
                                e.target.value,
                                'transferMethod'
                              );
                            }}
                          >
                            {TRANSFER_METHOD_OPTIONS.map((transferMethod) => (
                              <MenuItem
                                key={transferMethod.value}
                                value={transferMethod.id}
                              >
                                {transferMethod.value}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.transferMethod && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                    <Alert
                      severity="info"
                      className="w-full !mb-5 !mt-1 [&_.MuiAlert-message]:whitespace-pre-line"
                    >
                      {t('transferMethodInfo')}
                    </Alert>
                  </Grid>
                </>
              )}
            </>
          ) : null}
          {countryId && (
            <>
              {isCountryInEurope ? (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="currency">
                        {t('ccAccountCurrencyType')}
                      </InputLabel>
                      <Controller
                        name="accountCurrency"
                        control={control}
                        defaultValue={
                          company?.country !== 'COL'
                            ? COUNTRIES_CURRENCIES_OPTIONS[0].value
                            : 'USD'
                        }
                        render={({ field: { onChange, value } }) => (
                          <Select
                            labelId="accountCurrency"
                            id="accountCurrency"
                            defaultValue={COUNTRIES_CURRENCIES_OPTIONS[0].value}
                            label={t('ccAccountCurrencyType')}
                            value={value}
                            onChange={(val) => {
                              onChange(val);
                              setAccountCurrencySelected(
                                val.target.value as Currency
                              );
                            }}
                          >
                            {company?.country !== 'COL'
                              ? COUNTRIES_CURRENCIES_OPTIONS.map((currency) => (
                                  <MenuItem
                                    key={currency.key}
                                    value={currency.value}
                                  >
                                    {currency.key}
                                  </MenuItem>
                                ))
                              : [{ key: 'USD', value: 'USD' }].map(
                                  (currency) => (
                                    <MenuItem
                                      key={currency.key}
                                      value={currency.value}
                                    >
                                      {currency.key}
                                    </MenuItem>
                                  )
                                )}
                          </Select>
                        )}
                      />
                      {errors.accountCurrency && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="bankCountryCode">
                        {t('ccbankCountryCode')}
                      </InputLabel>
                      <Controller
                        name="bankCountryCode"
                        control={control}
                        defaultValue={
                          COUNTRIES_SWIFT_DROPDOWN_OPTIONS?.[0]?.value
                        }
                        render={({ field: { onChange } }) => (
                          <Select
                            labelId="bankCountryCode"
                            id="bankCountryCode"
                            defaultValue={
                              COUNTRIES_SWIFT_DROPDOWN_OPTIONS?.[0]?.value
                            }
                            label={t('ccbankCountryCode')}
                            value={bankCountryCodeValue}
                            size="medium"
                            disabled={isValidIban}
                            onChange={(e) => {
                              setBankCountryCodeValue(e.target.value);
                              setBankIsoCode(
                                COUNTRIES_SWIFT_DROPDOWN_OPTIONS.find(
                                  (item: any) => item.id === e.target.value
                                )?.isoCode
                              );
                              onChange(e);
                            }}
                            MenuProps={{
                              PaperProps: { sx: { maxHeight: 400 } },
                            }}
                          >
                            {COUNTRIES_SWIFT_DROPDOWN_OPTIONS.filter(
                              ({ isoCode }: { isoCode: string }) =>
                                !NON_SWIFT_COUNTRIES_ISO_CODES.includes(isoCode)
                            )
                              ?.sort(
                                (a: { isoCode: any }, b: { isoCode: any }) =>
                                  t(a.isoCode).localeCompare(
                                    t(b.isoCode),
                                    ['en', 'es'],
                                    {
                                      sensitivity: 'base',
                                    }
                                  )
                              )
                              .map((swiftCountry: any) => (
                                <MenuItem
                                  key={swiftCountry.id}
                                  value={swiftCountry.id}
                                >
                                  {t(swiftCountry.isoCode)}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                      />
                      {errors.bankCountryCode && (
                        <p className="text-orange-600 text-xs">
                          {t('defaultError')}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    id="bankName"
                    type="bankName"
                    label={t('bankName')}
                    error={!!errors.bankName}
                    {...register('bankName', { required: true })}
                  />
                  {errors.bankName && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    id="swiftCode"
                    type="text"
                    label={t('ccSwiftCode') as string}
                    error={!!errors.swiftCode}
                    {...register('swiftCode', {
                      required: true,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                      },
                    })}
                  />
                  {errors.swiftCode && errors.swiftCode.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.swiftCode && errors.swiftCode.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.swiftCode.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth className="!mb-3">
                  <TextField
                    id="accountNumber"
                    type="text"
                    label={t('ccAccountNumberIban')}
                    error={!!errors.accountNumber}
                    {...register('accountNumber', {
                      required: true,
                      pattern: {
                        value: onlyNumbersAndLettersRegex,
                        message: 'onlyNumbersAndLetters',
                      },
                      onChange: (e) => {
                        handleChangeAccountNumber(e.target.value);
                      },
                    })}
                  />
                  {isValidIban && !errors.accountNumber && (
                    <Alert className="!mt-2" severity="info">
                      {t('ibanValidation')}
                    </Alert>
                  )}
                  {errors.accountNumber &&
                    errors.accountNumber.type === 'required' && (
                      <p className="text-orange-600 text-xs">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors.accountNumber &&
                    errors.accountNumber.type === 'pattern' && (
                      <p className="text-orange-600 text-xs">
                        {t('onlyNumbersAndLetters')}
                      </p>
                    )}
                </FormControl>
              </Grid>
            </>
          )}
          {entity !== 'Person' &&
          accountCurrecySelected === 'USD' &&
          country === 'MX' ? (
            <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    id="address"
                    label={t('ccAddress')}
                    error={!!errors.address}
                    {...register('address', {
                      required: true,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                        poBox: (value) => validatePoBox(value, t),
                      },
                    })}
                  />
                  {errors.address && errors.address.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.address && errors.address.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.address.message as string}
                    </p>
                  )}
                  {errors.address && errors.address.type === 'poBox' && (
                    <p
                      className="text-orange-600 text-xs"
                      aria-describedby="address-error"
                    >
                      {errors.address.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    id="address2"
                    label={t('address2')}
                    error={!!errors.address2}
                    {...register('address2', {
                      required: true,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                        poBox: (value) => validatePoBox(value, t),
                      },
                    })}
                  />
                  {errors.address2 && errors.address2.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.address2 && errors.address2.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.address2.message as string}
                    </p>
                  )}
                  {errors.address2 && errors.address2.type === 'poBox' && (
                    <p
                      className="text-orange-600 text-xs"
                      aria-describedby="address2-error"
                    >
                      {errors.address2.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} className="!hidden">
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    {t('onbTaxIdUSA')}
                  </Typography>
                  <div className="flex w-full">
                    <TextField
                      autoComplete="off"
                      type="text"
                      className="custom-textfield w-[90%]"
                      size="small"
                      error={!!errors?.taxId}
                      helperText="XX-XXXXXXX"
                      placeholder="XX-XXXXXXX"
                      {...register('taxId', {
                        required: false, // TODO: Change to true
                        pattern: {
                          value: taxIdRegex,
                          message: '',
                        },
                      })}
                    />
                  </div>
                  {errors &&
                    errors.taxId &&
                    errors.taxId.type === 'required' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors &&
                    errors.taxId &&
                    errors.taxId.type === 'pattern' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="city"
                    label={t('city')}
                    error={!!errors.city}
                    {...register('city', {
                      required: true,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                      },
                    })}
                  />
                  {errors.city && errors.city.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.city && errors.city.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.city.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="state"
                    label={t('state')}
                    error={!!errors.state}
                    {...register('state', {
                      required: true,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                      },
                    })}
                  />
                  {errors.state && errors.state.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.state && errors.state.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.state.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="postCode"
                    label={t('postCode')}
                    error={!!errors.postCode}
                    {...register('postCode', {
                      required: true,
                      pattern: {
                        value: onlyNumbersRegex,
                        message: '',
                      },
                    })}
                  />
                  {errors &&
                    errors.postCode &&
                    errors.postCode.type === 'required' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors &&
                    errors.postCode &&
                    errors.postCode.type === 'pattern' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('onbOnlyNumberError')}
                      </p>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="rfc"
                    label={t('ccRFC')}
                    error={!!errors.rfc}
                    {...register('rfc', {
                      required: true,
                    })}
                  />
                  {errors && errors.rfc && errors.rfc.type === 'required' && (
                    <p className="text-orange-600 text-xs mt-1">
                      {t('defaultError')}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="providerRelation">
                    {t('ccProviderRelation')}
                  </InputLabel>
                  <Controller
                    name="providerRelation"
                    control={control}
                    defaultValue={providerRelation[0]}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        labelId="providerRelation"
                        id="providerRelation"
                        defaultValue={providerRelation[0]}
                        label={t('ccProviderRelation')}
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                        }}
                      >
                        {providerRelation.map((item) => (
                          <MenuItem key={item} value={item}>
                            {t(`cc${item}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.providerRelation && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                </FormControl>
              </Grid>
            </>
          ) : null}
        </Grid>
        <Grid
          item
          xs={12}
          className={clsx(
            'flex justify-between !flex-col md:!flex-row',
            country === 'MX' && '!mt-4'
          )}
        >
          {formStep > 0 && !error && (
            <Button
              appearance="secondary"
              className="mt-4"
              size="large"
              onClick={handleBack}
              type="button"
            >
              {t('backBtn')}
            </Button>
          )}
          <DataLoadingWrapper loadingData={loading} variant="spinner">
            {error && (error as any).response.data.code !== 9000 && (
              <p className="text-red-500 text-sm">{t('errorRetryMessage')}</p>
            )}
            <Button
              appearance="primary"
              size="large"
              className="mt-4"
              type="submit"
              disabled={
                (!data.countryId &&
                  data.country === Countries.USA &&
                  !!errorRoutingValidation) ||
                loadingRoutingValidation ||
                (!data.countryId &&
                  data.country === Countries.USA &&
                  (!!errorRoutingValidation || loadingRoutingValidation))
              }
            >
              {t(!error ? 'ccAddContact' : 'errorRetry')}
            </Button>
          </DataLoadingWrapper>
          {error && (error as any).response.data.code === 9000 ? (
            <Button
              appearance="secondary"
              size="large"
              className="mt-4"
              onClick={close}
            >
              {t('close')}
            </Button>
          ) : null}
        </Grid>
        {error && (error as any).response.data.code === 9000 ? (
          <Alert severity="warning" className="mt-4">
            {t('9000')}
          </Alert>
        ) : null}
      </form>
    </>
  );
};

export default BankData;
